import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Business, KeyboardArrowDown } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { ssoApi, SsoConfiguration } from "../../services/api/sso.api";
import { config } from "../../config/env";
import {
  isSafeInternalPath,
  peekReturnUrl,
  rememberReturnUrl,
} from "../../utils/return-url";
import { hardNavigation } from "../../utils/navigation";

// SSOT env (src/config/env.ts): канон REACT_APP_API_BASE_URL. Раньше читался
// Vite-only import.meta.env.VITE_API_URL, которого нет в CRA-проде → SSO-редирект
// всегда уходил на http://localhost:3001/api вместо боевого API.
const getApiBase = (): string => config.API_BASE_URL;

/**
 * Куда вернуться после SSO-round-trip (читает SsoCallbackPage). Приоритет:
 * 1) валидированный returnTo (напр. state.from от ProtectedRoute — передаёт LoginForm);
 * 2) УЖЕ сохранённый returnUrl (напр. от PublicEventRegistrationPage) — НЕ перезатирать
 *    (регрессия 2026-07-17: pathname с /auth/login затирал место гостя);
 * 3) текущий pathname — только если это не /auth/* (со страницы логина запоминать нечего).
 */
const rememberSsoReturn = (returnTo?: string) => {
  if (returnTo && isSafeInternalPath(returnTo)) {
    rememberReturnUrl(returnTo);
    return;
  }
  if (peekReturnUrl() !== null) {
    return;
  }
  const { pathname, search } = window.location;
  if (!pathname.startsWith("/auth")) {
    rememberReturnUrl(pathname + search);
  }
};

const initiateLogin = (config: SsoConfiguration, returnTo?: string) => {
  rememberSsoReturn(returnTo);
  const base = getApiBase();
  const path =
    config.protocol === "saml"
      ? `/auth/sso/saml/${config.id}/login`
      : `/auth/sso/oidc/${config.id}/login`;
  hardNavigation.assign(`${base}${path}`);
};

interface SsoLoginButtonProps {
  /** Внутренний путь возврата после SSO (валидируется isSafeInternalPath). */
  returnTo?: string | undefined;
}

export const SsoLoginButton: React.FC<SsoLoginButtonProps> = ({ returnTo }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["sso-configurations-public"],
    queryFn: () => ssoApi.listConfigurations(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const configs = (data?.data ?? []).filter((c) => c.isActive);

  if (isLoading || configs.length === 0) return null;

  if (configs.length === 1) {
    const config = configs[0]!;
    return (
      <Button
        fullWidth
        variant="outlined"
        startIcon={<Business />}
        onClick={() => initiateLogin(config, returnTo)}
        sx={{ textTransform: "none" }}
      >
        Войти через {config.name}
      </Button>
    );
  }

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<Business />}
        endIcon={<KeyboardArrowDown />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: "none" }}
      >
        Войти через корпоративный SSO
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {configs.map((config) => (
          <MenuItem
            key={config.id}
            onClick={() => {
              setAnchor(null);
              initiateLogin(config, returnTo);
            }}
          >
            <ListItemIcon>
              <Business fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={config.name}
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {config.protocol.toUpperCase()}
                  {config.allowedDomains?.length
                    ? ` · ${config.allowedDomains.join(", ")}`
                    : ""}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
